import { Call, DataChannelReceiver, Features } from "@azure/communication-calling";
import { useEffect, useState } from "react";
import { CommunicationUserIdentifier } from "@azure/communication-common";
import { useCallAdapter } from "../provider";
import { useApplicationInsights } from 'live-lesson/shared/util-insights-react';

// see
// https://learn.microsoft.com/de-de/azure/communication-services/quickstarts/voice-video-calling/get-started-data-channel?pivots=platform-web



// Initialize DataChannel
export const useDataChannel = (call: Call | undefined, onMessageReceived: (message: any) => void) => {

    const adapter = useCallAdapter();
    const { logTrace } = useApplicationInsights();

    const [sendCustomEvent, setSendCustomEvent] = useState<(eventName: string, eventData: any) => void>(() => () => {});

    useEffect(() => {
        if (!call) return;

        const textEncoder = new TextEncoder();
        const textDecoder = new TextDecoder();

        const dataChannelFeature = call.feature(Features.DataChannel);
        const dataChannelSender = dataChannelFeature.createDataChannelSender({
            priority: 'Normal',
            reliability: 'Durable',
            channelId: 1000,
        });

        // Function to send custom event
        const sendCustomEvent = (eventName: string, eventData: any) => {
            const message = ({ eventName, eventData, timestamp: new Date() });
            const encodedMessage = textEncoder.encode(JSON.stringify(message));

            dataChannelSender.sendMessage(encodedMessage);
        };
        setSendCustomEvent(() => sendCustomEvent);


        const channelReceiverCreatedHandler = (receiver: DataChannelReceiver) => {
            if (receiver.channelId === 1000) {
                receiver.on('close', () => {
                    logTrace(`data channel id = ${receiver.channelId} from ${JSON.stringify(receiver.senderParticipantIdentifier)} is closed`);
                });
                receiver.on('messageReady', () => {
                    const message = receiver.readMessage();
                    if (message) {
                        const text = textDecoder.decode(message.data);
                        const parsed = JSON.parse(text);
                        onMessageReceived(parsed);

                        logTrace(`DataChannel message from ${JSON.stringify(receiver.senderParticipantIdentifier)}:${text}`);
                    }
                });
            }
        };

        dataChannelFeature.on('dataChannelReceiverCreated', channelReceiverCreatedHandler);
        return () => {
            dataChannelFeature.off('dataChannelReceiverCreated', channelReceiverCreatedHandler);
        };
    }, [call]);

    return {
        sendCustomEvent,
    };
};

/**
 * Send a message using the data channel "1000" to the participant with the given id.
 * No other participants will receive this message.
 *
 * Message will indicate that this particular user is allowed to unmute his microphone.
 *
 * @param call
 * @param participantId
 */
export const sendIsAllowedToSpeak = (call: Call | undefined, participantId: CommunicationUserIdentifier) => {
    const sender = call?.feature(Features.DataChannel).createDataChannelSender({
        priority: 'Normal',
        reliability: 'Durable',
        channelId: 1000,
        participants: [participantId],
    });
    if (sender) {
        sender.sendMessage(new TextEncoder().encode(JSON.stringify({ isAllowedToSpeak: true })));
    }
};
